import { ShieldExclamationIcon } from "@heroicons/react/24/outline";
import type { ChannelVerdictBehavior } from "common";
import type { ClaudeChannelCardView } from "@via/hooks/useClaudeChannel";

export const OUTCOME_UNKNOWN_COPY = "Outcome unknown: the session moved on.";

interface ApprovalCardProps {
  card: ClaudeChannelCardView;
  sessionLabel: string;
  onVerdict: (behavior: ChannelVerdictBehavior) => void;
  onDismiss: () => void;
  onClear: () => void;
}

/** One permission request of a session. Only a click here produces a verdict (agent-6-2). */
export const ApprovalCard = ({
  card,
  sessionLabel,
  onVerdict,
  onDismiss,
  onClear,
}: ApprovalCardProps) => (
  <div
    data-testid="claude-approval-card"
    className="rounded-lg border border-amber-300 bg-amber-50 p-3 shadow-sm">
    <div className="flex items-start gap-2">
      <ShieldExclamationIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900">
          Claude wants to use {card.toolName}
        </p>
        <p className="truncate text-xs text-gray-500">{sessionLabel}</p>
        {card.description && (
          <p className="mt-1 text-sm text-gray-700">{card.description}</p>
        )}
        {card.inputPreview && (
          <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap break-words rounded bg-white p-2 font-mono text-xs text-gray-800 ring-1 ring-gray-200">
            {card.inputPreview}
          </pre>
        )}
      </div>
    </div>
    {card.outcome === "unknown" ? (
      <div className="mt-2 flex items-center justify-between gap-2">
        <p className="text-xs text-gray-600">{OUTCOME_UNKNOWN_COPY}</p>
        <button
          type="button"
          onClick={onClear}
          className="text-xs font-medium text-gray-600 underline hover:text-gray-900">
          Close
        </button>
      </div>
    ) : (
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => onVerdict("allow")}
          className="rounded-md bg-indigo-600 px-3 py-1 text-xs font-semibold text-white hover:bg-indigo-700">
          Allow
        </button>
        <button
          type="button"
          onClick={() => onVerdict("deny")}
          className="rounded-md bg-white px-3 py-1 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-300 hover:bg-red-50">
          Deny
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="ml-auto rounded-md px-3 py-1 text-xs font-medium text-gray-600 hover:bg-amber-100">
          Dismiss
        </button>
      </div>
    )}
  </div>
);
